import { createContext, MutableRefObject, useEffect, useRef, useState } from 'react'
import { FieldValues, FormProvider, SubmitHandler, UseFormReturn } from 'react-hook-form'
import { OvFormNavigationBlocker } from '../index.core.component'

export type IOvFormProvider<T extends FieldValues> = {
  children: React.ReactNode
  readOnly?: boolean
  loading?: boolean
  isCreate?: boolean
  enableNavigationBlocker?: boolean
  enableKeyboardShortcuts?: boolean
  onSubmit: SubmitHandler<Record<string, unknown>>
  onCancel?: () => void
  form: UseFormReturn<T>
}

export type IOvFormProviderMethods<T extends FieldValues> = UseFormReturn<T>

type IOvFormContext = {
  registerInput: (inputConfig: { name: string; validate?: string; label?: string }) => void
  onFormSubmit: SubmitHandler<Record<string, unknown>>
  allowNavigation?: boolean
  setAllowNavigation?: (allow: boolean) => void
  allowNavigationRef?: MutableRefObject<boolean>
} & Pick<IOvFormProvider<FieldValues>, 'readOnly' | 'loading'>

export const OvFormContext = createContext<IOvFormContext>({
  readOnly: false,
  loading: false,
  registerInput: () => void 0,
  onFormSubmit: () => void 0,
  allowNavigation: false,
  setAllowNavigation: () => void 0,
  allowNavigationRef: undefined,
})

const OvFormProvider = <T extends FieldValues>({
  children,
  readOnly,
  loading,
  onSubmit,
  onCancel,
  form,
  enableNavigationBlocker = true,
  enableKeyboardShortcuts = true,
}: IOvFormProvider<T>) => {
  const [allowNavigation, setAllowNavigation] = useState(false)
  const allowNavigationRef = useRef(false)

  const registerInput = (_input: Parameters<IOvFormContext['registerInput']>[0]) => {
    // Registration logic can be implemented here if needed
  }

  // Keyboard shortcuts: Ctrl/Cmd + S to save, Escape to cancel
  useEffect(() => {
    if (!enableKeyboardShortcuts || readOnly) return

    const handleKeyPress = (e: KeyboardEvent) => {
      // Save with Ctrl/Cmd + S
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault()
        form.handleSubmit(onSubmit)()
      }
      // Cancel with Escape
      if (e.key === 'Escape' && onCancel) {
        e.preventDefault()
        form.reset()
        onCancel()
      }
    }

    window.addEventListener('keydown', handleKeyPress)
    return () => window.removeEventListener('keydown', handleKeyPress)
  }, [enableKeyboardShortcuts, readOnly, form, onSubmit, onCancel])

  return (
    <FormProvider {...form}>
      <OvFormContext.Provider
        value={{
          registerInput,
          readOnly,
          loading,
          onFormSubmit: onSubmit,
          allowNavigation,
          setAllowNavigation,
          allowNavigationRef,
        }}>
        {enableNavigationBlocker ? <OvFormNavigationBlocker>{children}</OvFormNavigationBlocker> : children}
      </OvFormContext.Provider>
    </FormProvider>
  )
}

OvFormProvider.displayName = 'OvFormProvider'
export { OvFormProvider }
